import { useCallback } from "react";
import { useApi } from "./useApi";
import { useSessionSetAtom } from "../useSessionSetAtom";
import { createReadGuard } from "./_helpers/readGuard";
import type { DashboardResponse } from "@models/dashboard";
import { dashboardAtom } from "@/store/dashboardAtoms";

// Admin dashboard KPIs (E13-S2 / #243). Route is GET /dashboard (admin role
// enforced server-side) — NOT /admin/dashboard; the shipped endpoint wins
// over the story text.
const DASHBOARD_ENDPOINT = "/dashboard";

// Which read may publish the tiles (PR 369 review 1-3). Module-scope, like
// useProductApi's: the atom is module-scope. Without it an older poll (or a
// Retry's predecessor) answering last replaces newer counts. Latest-only
// (`isLatest`).
const dashboardReads = createReadGuard();

export const useDashboardApi = () => {
    const { callApi } = useApi();
    // Session-bound: a poll answering after sign-out must not refill them.
    const setDashboard = useSessionSetAtom(dashboardAtom);

    // skipErrorNotification: the page polls every 60s and renders its own
    // inline ErrorState, so the generic toast would fire every minute during
    // an outage (and double-signal a failed first load).
    const getDashboard = useCallback(async (): Promise<DashboardResponse> => {
        const { isLatest } = dashboardReads.begin();

        const data = await callApi<DashboardResponse>(DASHBOARD_ENDPOINT, {
            skipErrorNotification: true,
        });
        if (isLatest()) setDashboard(data);

        return data;
    }, [callApi, setDashboard]);

    return {
        getDashboard,
    };
};
